/* ============================================================================
   More n Mine — declarative scroll parallax engine
   ----------------------------------------------------------------------------
   Mark any element with data-px and it becomes a depth layer. Everything is
   driven from attributes so sections stay pure Liquid and merchants can tune
   depth from the theme editor without touching JS.

     data-px            enrols the element (value ignored)
     data-px-y="-8"     vertical travel at each extreme, in % of viewport height
     data-px-x="3"      horizontal travel, in % of viewport width

   Travel is measured against the viewport rather than the element's own box, so
   a "6" reads as the same physical distance whether it sits on a 40px gem or a
   full-bleed backdrop. That makes depth ordering tunable by eye.

     data-px-scale=".12" scale delta — 1 at centre, 1±.12 at the extremes
     data-px-rot="-4"   rotation in degrees
     data-px-opacity=".4" lowest opacity at the extremes (1 at centre)
     data-px-blur="3"   blur in px at the extremes
     data-px-ease=".08" lerp factor; lower = heavier, more trailing (default .1)
     data-px-mouse=".5" additional pointer tilt, as a multiple of the base travel
     data-px-clamp      stop animating once the element leaves the viewport

   Progress is centred: an element dead-centre in the viewport sits at 0, fully
   entering from the bottom at -1, fully exited past the top at +1. That keeps
   layers symmetric — they read as depth rather than as a one-way slide.

   Deliberately independent of GSAP/ScrollTrigger. The existing .parallax section
   image (#pxImg) is still driven by ScrollTrigger in script.js; it carries no
   data-px attribute, so the two never write to the same element.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = false;
  try {
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  // Honour the theme-wide low-power flag set in theme.liquid. Weak devices get
  // the finished layout with no per-frame transform work at all.
  var DISABLED = reduced || window.ARC_LOWPOWER === true;

  var layers = [];      // every enrolled element
  var active = [];      // subset currently intersecting the viewport
  var running = false;
  var mx = 0, my = 0;   // pointer position, -1..1 from viewport centre
  var tmx = 0, tmy = 0; // smoothed pointer

  function num(el, attr, dflt) {
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? dflt : v;
  }

  function enrol(el) {
    if (el.__px) return;
    var L = {
      el: el,
      y: num(el, 'data-px-y', 0),
      x: num(el, 'data-px-x', 0),
      scale: num(el, 'data-px-scale', 0),
      rot: num(el, 'data-px-rot', 0),
      fade: num(el, 'data-px-opacity', 1),
      blur: num(el, 'data-px-blur', 0),
      ease: num(el, 'data-px-ease', 0.1),
      mouse: num(el, 'data-px-mouse', 0),
      clamp: el.hasAttribute('data-px-clamp'),
      cur: 0,        // smoothed progress
      target: 0,     // raw progress this frame
      visible: false
    };
    el.__px = L;
    layers.push(L);
  }

  // --- progress -------------------------------------------------------------
  // -1 = element's centre is one viewport-height below the middle of the screen
  //  0 = element centre aligned with screen centre
  // +1 = one viewport-height above
  function progressOf(L) {
    var r = L.el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var mid = r.top + r.height / 2;
    var p = (vh / 2 - mid) / (vh / 2 + r.height / 2);
    if (L.clamp) p = Math.max(-1, Math.min(1, p));
    return p;
  }

  function apply(L) {
    var p = L.cur;
    var el = L.el;
    var vh = window.innerHeight || 800;
    var vw = window.innerWidth || 1200;

    // Pointer tilt rides on top of the scroll offset. It is deliberately a
    // fixed viewport fraction rather than a multiple of the layer's scroll
    // travel — otherwise layers authored with y:0 would never tilt at all.
    var mvx = L.mouse ? tmx * L.mouse * vw * 0.014 : 0;
    var mvy = L.mouse ? tmy * L.mouse * vh * 0.014 : 0;

    var tx = (p * L.x * vw / 100) + mvx;
    var ty = (p * L.y * vh / 100) + mvy;

    var t = 'translate3d(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px,0)';
    // Peaks at the centre of the transit and relaxes to 1 at either extreme.
    if (L.scale) t += ' scale(' + (1 + (1 - Math.abs(p)) * L.scale).toFixed(4) + ')';
    if (L.rot) t += ' rotate(' + (p * L.rot).toFixed(3) + 'deg)';
    el.style.transform = t;

    if (L.fade < 1) {
      el.style.opacity = (1 - Math.abs(p) * (1 - L.fade)).toFixed(3);
    }
    if (L.blur) {
      el.style.filter = 'blur(' + (Math.abs(p) * L.blur).toFixed(2) + 'px)';
    }
  }

  var lastScroll = -1;
  var dirty = true;   // forces a recompute after layout-changing events

  function frame() {
    if (!active.length) { running = false; return; }

    // The loop stays alive for as long as anything is on screen, and decides
    // per-frame whether there is real work to do. Driving it purely from scroll
    // events was fragile: a coalesced or missed event, or a layout shift from a
    // lazy-loaded image, left every layer frozen at a stale offset with nothing
    // to wake it. Reading scrollY once a frame is far cheaper than the bug.
    var sy = window.pageYOffset || document.documentElement.scrollTop || 0;
    var scrolled = sy !== lastScroll;
    var pointerMoving = Math.abs(mx - tmx) > 0.0005 || Math.abs(my - tmy) > 0.0005;

    var settling = false;
    for (var i = 0; i < active.length; i++) {
      if (Math.abs(active[i].target - active[i].cur) > 0.0002) { settling = true; break; }
    }

    if (scrolled || pointerMoving || settling || dirty) {
      lastScroll = sy;
      dirty = false;
      tmx += (mx - tmx) * 0.06;
      tmy += (my - tmy) * 0.06;
      for (var j = 0; j < active.length; j++) {
        var L = active[j];
        L.target = progressOf(L);
        L.cur += (L.target - L.cur) * L.ease;
        apply(L);
      }
    }

    requestAnimationFrame(frame);
  }

  function kick() {
    if (running || !active.length) return;
    running = true;
    requestAnimationFrame(frame);
  }

  function invalidate() { dirty = true; kick(); }

  // --- lifecycle ------------------------------------------------------------
  var io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var L = entries[i].target.__px;
        if (!L) continue;
        if (entries[i].isIntersecting) {
          if (!L.visible) {
            L.visible = true;
            active.push(L);
            L.el.style.willChange = 'transform';
            // Snap to the correct offset on entry so a layer never pops in from
            // its neutral position.
            L.cur = progressOf(L);
            apply(L);
          }
        } else if (L.visible) {
          L.visible = false;
          var k = active.indexOf(L);
          if (k > -1) active.splice(k, 1);
          L.el.style.willChange = '';
        }
      }
      kick();
    }, { rootMargin: '15% 0px 15% 0px' });
  }

  function scan(root) {
    var els = (root || document).querySelectorAll('[data-px]');
    for (var i = 0; i < els.length; i++) {
      enrol(els[i]);
      if (io) {
        io.observe(els[i]);
      } else {
        // No IntersectionObserver — treat everything as active.
        var L = els[i].__px;
        if (!L.visible) { L.visible = true; active.push(L); }
      }
    }
    kick();
  }

  function init() {
    if (DISABLED) {
      // Leave the markup exactly as authored; sections are styled so their
      // resting state is the finished composition.
      document.documentElement.classList.add('px-off');
      return;
    }
    document.documentElement.classList.add('px-on');
    scan(document);

    // The rAF loop notices scrolling on its own; this only restarts it after it
    // has shut down because nothing was on screen.
    window.addEventListener('scroll', kick, { passive: true });

    window.addEventListener('resize', function () {
      for (var i = 0; i < active.length; i++) active[i].cur = progressOf(active[i]);
      invalidate();
    }, { passive: true });

    // Late-loading media reflows the page without moving the scroll position,
    // which would otherwise leave layers pinned to pre-reflow geometry.
    window.addEventListener('load', invalidate);
    document.addEventListener('lazyloaded', invalidate);

    // Pointer tilt, desktop only — on touch there is no hover to read.
    if (window.matchMedia('(hover:hover) and (pointer:fine)').matches) {
      window.addEventListener('mousemove', function (e) {
        mx = (e.clientX / window.innerWidth) * 2 - 1;
        my = (e.clientY / window.innerHeight) * 2 - 1;
        kick();
      }, { passive: true });
    }

    // Theme editor: sections are swapped in without a page load.
    document.addEventListener('shopify:section:load', function (e) {
      scan(e.target);
      invalidate();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Exposed so other scripts (or a section's inline JS) can enrol late-injected
  // markup — e.g. products rendered after a fetch.
  window.MnMParallax = { scan: scan, disabled: DISABLED };
})();
